import React, { useMemo } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';

import {
    MdCheckCircle,
    MdEmail,
    MdOutlineError,
    MdSend,
    MdSimCardDownload,
} from 'react-icons/md';
import Card from 'components/card';
import CreateButton from 'components/buttons/CreateButton';
import InvoiceMenu from './invoiceMenu';
import SearchIcon from 'components/icons/SearchIcon';
import Select from 'components/fields/Select';
import GoBack from 'components/actions/GoBack';

const columnHelper = createColumnHelper<any>();

const SkeletonRow = React.memo(() => {
    return (
        <tr>
            {Array.from({ length: 7 }).map((_, i) => (
                <td key={i} className="min-w-[150px] py-3 pr-4">
                    <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-white/20"></div>
                </td>
            ))}
        </tr>
    );
});

const SendMassInvoice = () => {
    return (
        <Card
            extra={
                'w-full pb-[10px] mb-3 pt-[10px] px-[29px] h-fit bg-blue-200 shadow shadow-3xl border'
            }
        >
            {/* Header */}
            <div className="mt-1">
                <p className="text-xl font-semibold text-navy-700 dark:text-white">
                    Envoyer vos factures ✉️
                </p>
                <p className="mt-3 mb-3 text-base text-gray-600">
                    Toutes les factures non-envoyées de toutes les périodes
                    confondues seront envoyées. (
                    <b className="text-gray-700">x factures</b> )
                </p>
                <CreateButton
                    value={`Envoyer`}
                    extra="my-auto h-10"
                    onClick={() => {}}
                />
            </div>
        </Card>
    );
};

export default function InvoicesSkelettonTable() {
    const [sorting, setSorting] = React.useState<SortingState>([]);
    const [data] = React.useState<any>([]);

    const nav = useNavigate();

    const columns = useMemo(() => [
        columnHelper.accessor('n', {
            id: 'n',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    N° Facture
                </p>
            ),
            cell: (info) => (
                <>
                    <p className="text-sm text-navy-700 dark:text-white">
                        #{info.getValue()['invoiceNumber']}
                    </p>
                    <p className="text-sm font-bold text-navy-700 dark:text-white">
                        {info.getValue()['date']}
                    </p>
                </>
            ),
        }),
        columnHelper.accessor('customer', {
            id: 'customer',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Client
                </p>
            ),
            cell: (info) => (
                <div
                    className="flex gap-1 hover:cursor-pointer hover:underline"
                    onClick={() =>
                        window.open(
                            `/customer/${info.row.original.customerId}`,
                            '_blank',
                        )
                    }
                >
                    <p className="text-sm text-toola-500 dark:text-white">
                        {info.getValue()}
                    </p>
                </div>
            ),
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Statut
                </p>
            ),
            cell: (info) => {
                const status = info.getValue();
                const baseStyle =
                    'flex rounded-xl p-1 px-3 gap-1 text-xs font-bold text-white';
                switch (status) {
                    case 'draft':
                        return (
                            <div className="flex items-center">
                                <MdOutlineError className="text-amber-500 me-1 dark:text-amber-300" />
                                <p className="text-sm font-bold text-navy-700 dark:text-white">
                                    Brouillon
                                </p>
                            </div>
                        );
                    case 'generated':
                        return (
                            <div className={`${baseStyle} bg-red-400`}>
                                Non envoyée
                            </div>
                        );
                    case 'sent':
                        return (
                            <div className={`${baseStyle} bg-amber-500`}>
                                <MdEmail className="text-white me-1" />
                                Envoyée
                            </div>
                        );
                    case 'paid':
                        return (
                            <div className={`${baseStyle} bg-green-500`}>
                                <MdCheckCircle className="text-white" />
                                Payée
                            </div>
                        );
                    case 'canceled':
                        return (
                            <div className={`${baseStyle} bg-gray-300`}>
                                Annulée
                            </div>
                        );
                    case 'credit-note':
                        return (
                            <div className={`${baseStyle} bg-blue-400`}>
                                Facture d'avoir
                            </div>
                        );
                    default:
                        return null;
                }
            },
        }),
        columnHelper.accessor('leftToPay', {
            id: 'leftToPay',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Reste à payer
                </p>
            ),
            cell: (info) => {
                const val = info.getValue();
                const className =
                    val === 0
                        ? 'text-green-500'
                        : val < 0
                          ? 'text-navy-700'
                          : 'text-red-500';
                return (
                    <p
                        className={`text-sm font-bold ${className} dark:text-white`}
                    >
                        {val?.toFixed(2) ?? '-- --'} €
                    </p>
                );
            },
        }),
        columnHelper.accessor('totalHT', {
            id: 'totalHT',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total HT
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('totalTTC', {
            id: 'totalTTC',
            header: () => (
                <p className="text-sm font-bold text-gray-600 dark:text-white">
                    Total TTC
                </p>
            ),
            cell: (info) => (
                <p className="text-sm font-bold text-navy-700 dark:text-white">
                    {info.getValue().toFixed(2)} €
                </p>
            ),
        }),
        columnHelper.accessor('_id', {
            id: '_id',
            header: () => {},
            cell: (info) => {
                const id = info.getValue();
                const invoice = data.find((inv: any) => inv._id === id);
                return (
                    <div className="flex items-center gap-5">
                        <MdSimCardDownload
                            title="Télécharger la facture"
                            className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                            onClick={() => {}}
                        />
                        <MdSend
                            title="Envoyer par mail"
                            className="text-toola-500 h-6 w-6 hover:cursor-pointer hover:text-toola-700"
                            onClick={() => {}}
                        />
                        <InvoiceMenu
                            disabled={['canceled'].includes(invoice?.status)}
                            isLastItem={[
                                data.length - 1,
                                data.length - 2,
                            ].includes(data.findIndex((inv: any) => inv._id === id))}
                            invoice={invoice}
                            openHistory={() => {}}
                            updateInvoiceStatus={() => {}}
                            createCreditNote={() => {}}
                        />
                    </div>
                );
            },
        }),
    ], [data]); // eslint-disable-line react-hooks/exhaustive-deps

    const table = useReactTable({
        data,
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <>
            <SendMassInvoice />
            <GoBack />
            <Card extra="mt-1 w-full px-6 pb-6 sm:overflow-x-auto border">
                <div className="relative flex items-center justify-between pt-4">
                    <div className="text-xl font-bold text-navy-700 dark:text-white">
                        Vos dernières factures
                    </div>
                    <div className="flex items-center gap-4">
                        <CreateButton
                            value="Facturer mes clients"
                            extra="my-auto h-10"
                            onClick={() => nav('/generate-invoice')}
                        />
                    </div>
                </div>
                <p className="ml-1 text-sm text-toola-500 hover:underline hover:cursor-pointer">
                    Gestionnaire des impayés
                </p>

                {/* Filters */}
                <div className="mt-2 grid grid-cols-12 gap-5">
                    <div className="mt-4 col-span-3">
                        <div className="flex h-[30px] flex-grow items-center rounded-xl bg-lightPrimary text-xs text-gray-600">
                            <SearchIcon />
                            <input
                                type="text"
                                placeholder="Client, N° Facture, ..."
                                value={null}
                                onChange={(e) => {}}
                                className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none"
                            />
                        </div>
                    </div>
                    <div className="col-span-2">
                        <p className="text-xs ml-1">Statut</p>
                        <Select
                            extra="!mt-0 pt-[5px] h-[30px] !w-[200px]"
                            options={[
                                { label: 'Tous', value: 'all' },
                                { label: 'Non envoyée', value: 'generated' },
                                { label: 'Envoyée', value: 'sent' },
                                { label: 'Payée', value: 'paid' },
                                { label: 'Avoir', value: 'credit-note' },
                                { label: 'Annulée', value: 'canceled' },
                            ]}
                            displayKey="label"
                            onSelect={(e: any) => {}}
                            required={false}
                        />
                    </div>
                </div>

                <div className="mt-4 overflow-x-scroll xl:overflow-x-hidden">
                    <table className="w-full">
                        <thead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <tr
                                    key={headerGroup.id}
                                    className="!border-px !border-gray-400"
                                >
                                    {headerGroup.headers.map((header) => (
                                        <th
                                            key={header.id}
                                            colSpan={header.colSpan}
                                            onClick={header.column.getToggleSortingHandler()}
                                            className="cursor-pointer border-b border-gray-200 pt-4 pb-2 pr-4 text-start dark:border-white/30"
                                        >
                                            <div className="items-center justify-between text-xs text-gray-200">
                                                {flexRender(
                                                    header.column.columnDef
                                                        .header,
                                                    header.getContext(),
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {data.length === 0
                                ? Array.from({ length: 15 }).map((_, i) => (
                                      <SkeletonRow key={i} />
                                  ))
                                : table.getRowModel().rows.map((row) => (
                                      <tr key={row.id}>
                                          {row.getVisibleCells().map((cell) => (
                                              <td
                                                  key={cell.id}
                                                  className="min-w-[150px] border-white/0 py-3 pr-4"
                                              >
                                                  {flexRender(
                                                      cell.column.columnDef
                                                          .cell,
                                                      cell.getContext(),
                                                  )}
                                              </td>
                                          ))}
                                      </tr>
                                  ))}
                        </tbody>
                    </table>
                </div>
            </Card>
        </>
    );
}
